import _ from "lodash";

/**
 * Merge list of posts
 * @param {*} data
 * @param {*} payload
 */
export function mergeListOfPosts(data, payload) {
  return _.uniqBy([...data, ...payload], "post_id");
}

/**
 * Sort files within a post's postFiles array so that videos come first
 * @param {object} post - Post object with postFiles array
 * @returns {object} Post object with sorted postFiles (videos first)
 */
export function sortPostFilesWithVideosFirst(post) {
  if (!post || !post.postFiles || !Array.isArray(post.postFiles)) return post;
  
  return {
    ...post,
    postFiles: [...post.postFiles].sort((a, b) => {
      const aIsVideo = a.file_type === 'video';
      const bIsVideo = b.file_type === 'video';
      
      // Videos first (true comes before false)
      if (aIsVideo && !bIsVideo) return -1;
      if (!aIsVideo && bIsVideo) return 1;
      
      // If both are videos or both are not videos, maintain original order
      return 0;
    }),
  };
}

/**
 * Sort posts so that videos come first
 * A post is considered a video post if it has at least one file with file_type === 'video'
 * Also sorts files within each post so videos come first
 * @param {array} posts - Array of post objects
 * @returns {array} Sorted array with video posts first, and files within each post sorted
 */
export function sortPostsWithVideosFirst(posts) {
  if (!posts || !Array.isArray(posts)) return posts;
  
  return posts.map(post => sortPostFilesWithVideosFirst(post)).sort((a, b) => {
    const aHasVideo = a.postFiles?.some((file) => file.file_type === 'video') || false;
    const bHasVideo = b.postFiles?.some((file) => file.file_type === 'video') || false;
    
    // Videos first (true comes before false)
    if (aHasVideo && !bHasVideo) return -1;
    if (!aHasVideo && bHasVideo) return 1;
    
    // If both are videos or both are not videos, maintain original order
    return 0;
  });
}

/**
 * Merge list of users
 * @param {*} data
 * @param {*} payload
 */
 export function mergeListOfUsers(data, payload) {
  return _.uniqBy([...data, ...payload], "id");
}

/**
 * Modify post data likes
 * @param {array} data
 * @param {object} payload
 */
export function modifyPostLike(data, payload) {
  return (
    data?.map((post) => {
      if (post.post_id.toString() === payload.post_id.toString()) {
        return {
          ...post,
          is_user_liked: payload.is_user_liked ? payload.is_user_liked : ( payload.action === "add" ? 1 : 0 ),
          total_likes: 
          payload.post_total_likes ? payload.post_total_likes :
            (payload.action === "add"
              ? post.total_likes + 1
              : post.total_likes - 1),
        };
      }
      return post;
    }) || []
  );
}

/**
 * Modify post comment count
 * @param {array} data
 * @param {object} payload
 */
export function modifyPostCommentCount(data, payload) {
  return (
    data?.map((post) => {
      if (post.post_id.toString() === payload.post_id.toString())
        return { ...post, total_comments: post.total_comments + 1 };
      return post;
    }) || []
  );
}

export function incrementPostTipCount(data, post_id) {
  return (
    data?.map((post) => {
      if (post.post_id.toString() === post_id?.toString())
        return { ...post, total_tips: post.total_tips + 1 };
      return post;
    }) || []
  );
}

/**
 * Modify post comments
 * @param {array} data
 * @param {object} payload
 */
export function modifyPostComments(data, payload) {
  return (
    data?.map((post) => {
      if (post.post_id.toString() === payload.post_id.toString()) {
        const existingComments = Array.isArray(post.post_comments) ? post.post_comments : [];
        return { ...post, post_comments: [...existingComments, payload] };
      }
      return post;
    }) || []
  );
}

/**
 * Modify post bookmark status
 * @param {array} data
 * @param {object} payload
 */
export function modifyPostBookmarkStatus(data, payload) {
  return (
    data?.map((post) => {
      if (post.post_id.toString() === payload.post_id.toString())
        return {
          ...post,
          is_user_bookmarked: post.is_user_bookmarked === 1 ? 0 : 1,
        };
      return post;
    }) || []
  );
}

/**
 * Replace updated post
 * @param {array} data
 * @param {object} payload
 */
export function replaceUpdatePost(data, payload) {
  return data?.map((item) =>
    item.post_id.toString() === payload.post_id.toString() ? payload : item
  ) || [];
}

/**
 * Modify single post data likes
 * @param {array} data
 * @param {object} payload
 */
export function modifySinglePostLike(data, payload) {
  if (!data) return {};
  return {
    ...data,
    is_user_liked: payload.action === "add" ? 1 : 0,
    total_likes:
      payload.action === "add" ? data.total_likes + 1 : data.total_likes - 1,
  };
}

/**
 * Modify single post data likes
 * @param {array} data
 * @param {object} payload
 */
 export function updateSinglePostLike(data, payload) {
  if (!data) return {};
  return {
    ...data,
    is_user_liked: payload.is_user_liked,
    total_likes: payload.post_total_likes,
  };
}
/**
 * Increment single post tips
 * @param {*} data
 */
export function incrementSinglePostTips(data) {
  if (!data) return {};
  return {
    ...data,
    total_tips: data.total_tips + 1,
  };
}

/**
 * modify single post comment count
 *
 * @param {array} data
 * @param {object} payload
 */
export function modifySinglePostCommentCount(data, payload) {
  if (!data) return {};
  return {
    ...data,
    total_comments: data.total_comments + 1,
  };
}

/**
 * modify single post comments
 *
 * @param {array} data
 * @param {object} payload
 */
export function modifySinglePostComments(data, payload) {
  if (!data) return {};
  return {
    ...data,
    post_comments: [...data.post_comments, payload],
  };
}

/**
 * modify single post bookmark status
 *
 * @param {object} data
 * @param {object} payload
 */
export function modifySinglePostBookmarkStatus(data, payload = {}) {
  if (!data || !data.post_id) return {};

  if (payload.post_id && payload.post_id.toString() == data.post_id.toString())
    return {
      ...data,
      is_user_bookmarked: data.is_user_bookmarked === 1 ? 0 : 1,
    };
  
    return data
}

/**
 * Generate random string
 *
 * @param {length} number random string
 */
export function randomString() {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues === 'function') {
    const array = new Uint32Array(2);
    window.crypto.getRandomValues(array);
    return array.join('-');
  } else {
    // Fallback to a less secure method using Math.random
    return (Math.random() + 1).toString(6).substring(7);
  }
}


/**
 * Generate element by attribute
 *
 * @param component
 * @param attribute component wrapper
 */
export function findByAttributes(component, attr) {
  const wrapper = component.find(`[data-test='${attr}']`);
  return wrapper;
}

/**
 * limit the input length
 *
 * @param event
 * @param length string
 */
export function handleInputMaxLength(e, length) {
  return (e.target.value = e.target.value.slice(0, length));
}

/**
 * get the button label for forgot password
 *
 * @param isInvalidEmail
 * @param isManualLogin string
 */

export function getForgotPasswordButtonLabel(isInvalidEmail, isManualLogin) {
  if (isInvalidEmail && isManualLogin) {
    return "Register";
  }
  if (!isInvalidEmail && !isManualLogin) {
    return "Login";
  }
  return "Submit";
}

/*
 * update post like count
 *
 * @param {array} data
 * @param {object} post {array} updatedData
 */

export function updatePostLike(data = [], post) {
  const postIndex = _.findIndex(data, { post_id: Number(post.post_id) });
  if (data[postIndex]) {
    data[postIndex].total_likes = post.post_total_likes;
    data[postIndex].is_user_liked = post.is_user_liked;
  }
  return data;
}

/*
 * Format amount
 *
 * @param {string} amount {string} formatted amount
 */

export function formatAmount(amount) {
  return amount?.toLocaleString("en-US", {
    minimumFractionDigits: 2,
  });
}

/*
 * Capitalize first letter
 *
 * @param {string}
 */

export function capitalizeFirstLetter(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/*
 * Format phone number
 *
 * @param {string}
 */

export function removePhoneCode(number, countryCode) {
  let finalNumber = number;
  for (let i = 0; i < countryCode.replace("+", "").length; i++) {
    finalNumber = finalNumber.replace(number[i], "");
  }
  return finalNumber;
}

/**
 * Modify post has story status
 * @param {array} data
 * @param {object} payload
 */
 export function modifyPostHasStoryStatus(data, payload) {
  return (
    data?.map((post) => {
      if (post.post_id === payload.post_id)
        return { ...post, has_story: 1 };
      return post;
    }) || []
  );
}

/**
 * Modify subscription status
 * 
 * @param {array} data
 * @param {object} payload
 */
export function modifyPostOnSubscribedToPerformer(data, payload) {
  return (
    data?.map((post) => {
      if (post.user_id === payload.performer_id)
        return {
          ...post,
          is_user_subscribed: true,
        };
      return post;
    }) || []
  );
}

/**
 * modify single post subscription status
 *
 * @param {object} data
 * @param {object} payload
 */
export function modifySinglePostOnSubscribedToPerformer(data, payload) {
  if (!data || !data.post_id) return {};

  return {
    ...data,
    is_user_subscribed: data.user_id === payload.performer_id ? true : data.is_user_subscribed,
  }
}

/**
 * modify single post bookmark status
 *
 * @param {object} data
 * @param {object} payload
 */
 export function modifySinglePostHasStoryStatus(data, payload = {}) {
  if (!data || !data.post_id) return {};

  if (payload.post_id && payload.post_id.toString() == data.post_id.toString())
    return {
      ...data,
      has_story: 1,
    };
  
    return data
}

/**
 * modify post on update
 * @param {array} data
 * @param {object} payload
 */
 export function modifyPostOnUpdate(data, payload) {
  return (
    data?.map((post) => {
      if (post.post_id === payload.post_id)
        return { ...post, ...payload };
      return post;
    }) || []
  );
}

/**
 * modify post on update
 *
 * @param {object} data
 * @param {object} payload
 */
 export function modifySinglePostOnUpdate(data, payload = {}) {
  if (!data || !data.post_id) return {};

  if (payload.post_id && payload.post_id.toString() == data.post_id.toString())
    return {
      ...data,
      ...payload,
    };
  
    return data
}

/**
 * Merge list of story
 * @param {*} data
 * @param {*} payload
 * @param {*} key
 */
export function mergeListOfStory(data, payload, key= 'id') {
  return _.uniqBy([...data, ...payload], key);
}

/**
 * Modify profile story on read 
 * @param {array} data
 * @param {object} payload
 */
export function modifyHomeStoryListOnRead(data, payload) {
  const finalData = data.map(homeStory => {
    return {
      ...homeStory,
      stories : homeStory.stories?.map((story) => {
        if (story.id === payload.story_id)
          return { ...story, is_read: true };
        return story;
      }) || []
    }
  }).filter(homeStory => homeStory.stories.length > 0)
 
  return finalData
}

/**
 * Modify home story on delete 
 * @param {array} data
 * @param {object} payload
 */
export function modifyHomeStoryListOnDelete(data, payload) {
  const finalData = data.map(homeStory => {
    return {
      ...homeStory,
      stories : homeStory.stories?.filter((story) => {
        return story.id !== payload.story_id
      }) || []
    }
  }).filter(homeStory => homeStory.stories.length > 0)
  return finalData
}

/**
 * Modify profile story on read 
 * @param {array} data
 * @param {object} payload
 */
export function modifyProfileStoryListOnRead(data, payload) {
  return (
    data?.map((story) => {
      if (story.id === payload.story_id)
        return { ...story, is_read: true };
      return story;
    }) || []
  );
}

/**
 * Modify profile story on delete 
 * @param {array} data
 * @param {object} payload
 */
export function modifyProfileStoryListOnDelete(data, payload) {
  
  return (
    data?.filter((story) => {
      return story.id !== payload.story_id
    }) || []
  );
}

/**
 * Find post_unique_id by searching for a post with the given post_id across all Redux reducers
 * Searches in: VideoStore, Home, Post (posts array and singlePost), Bookmarks (all, photo, video), OtherUser
 * @param {object} state - The Redux state object
 * @param {number|string} postId - The post_id to search for
 * @returns {string|null} The post_unique_id or unique_id if found, null otherwise
 */
export function findPostUniqueIdByPostId(state, postId) {
  if (!state || !postId) return null;
  
  // Helper function to extract post_unique_id from a post
  const getPostUniqueId = (post) => {
    return post?.post_unique_id || post?.unique_id || null;
  };
  
  // Check VideoStore
  const videoStorePost = state.videoStore?.items?.data?.posts?.find(
    (post) => post.post_id?.toString() === postId.toString()
  );
  if (videoStorePost) {
    return getPostUniqueId(videoStorePost);
  }
  
  // Check Home posts
  const homePost = state.home?.postList?.data?.posts?.find(
    (post) => post.post_id?.toString() === postId.toString()
  );
  if (homePost) {
    return getPostUniqueId(homePost);
  }
  
  // Check Post reducer - posts array (profile posts)
  const postReducerPost = state.post?.posts?.data?.data?.find(
    (post) => post.post_id?.toString() === postId.toString()
  );
  if (postReducerPost) {
    return getPostUniqueId(postReducerPost);
  }
  
  // Check Post reducer - singlePost
  const singlePost = state.post?.singlePost?.data?.post;
  if (singlePost && singlePost.post_id?.toString() === postId.toString()) {
    return getPostUniqueId(singlePost);
  }
  
  // Check Bookmarks - all bookmarks
  const bookmarkPost = state.bookmark?.bookmark?.data?.posts?.find(
    (post) => post.post_id?.toString() === postId.toString()
  );
  if (bookmarkPost) {
    return getPostUniqueId(bookmarkPost);
  }
  
  // Check Bookmarks - photo bookmarks
  const bookmarkPhotoPost = state.bookmark?.bookmarkPhoto?.data?.posts?.find(
    (post) => post.post_id?.toString() === postId.toString()
  );
  if (bookmarkPhotoPost) {
    return getPostUniqueId(bookmarkPhotoPost);
  }
  
  // Check Bookmarks - video bookmarks
  const bookmarkVideoPost = state.bookmark?.bookmarkVideo?.data?.posts?.find(
    (post) => post.post_id?.toString() === postId.toString()
  );
  if (bookmarkVideoPost) {
    return getPostUniqueId(bookmarkVideoPost);
  }
  
  // Check OtherUser reducer - profile posts
  const otherUserPost = state.otherUser?.userPosts?.data?.posts?.find(
    (post) => post.post_id?.toString() === postId.toString()
  );
  if (otherUserPost) {
    return getPostUniqueId(otherUserPost);
  }
  
  return null;
}
